import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Loader2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { AddressAutocomplete } from "@/components/measurements/AddressAutocomplete";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { useAuth } from "@/hooks/useAuth";

const CreateJob = () => {
  const navigate = useNavigate();
  const { user, orgId, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const { isLoaded } = useGoogleMaps();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const fetchTrades = async () => {
      const { data } = await supabase
        .from("trades")
        .select("*")
        .eq("is_active", true)
        .order("name");
      setAvailableTrades(data || []);
    };
    fetchTrades();
  }, []);
  
  const [jobName, setJobName] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [jobType, setJobType] = useState("");
  
  // Trade selection
  const [selectedTrades, setSelectedTrades] = useState<string[]>(["roofing"]);
  const [availableTrades, setAvailableTrades] = useState<any[]>([]);
  
  // Optional fields
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [insuranceCompany, setInsuranceCompany] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [adjusterName, setAdjusterName] = useState("");
  const [adjusterPhone, setAdjusterPhone] = useState("");
  const [adjusterEmail, setAdjusterEmail] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [roofType, setRoofType] = useState("");
  const [roofAge, setRoofAge] = useState("");
  
  const [insuranceOpen, setInsuranceOpen] = useState(false);
  const [propertyOpen, setPropertyOpen] = useState(false);

  const generateJobNumber = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'JOB-';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handlePlaceSelected = (place: { address: string; lat: number; lng: number }) => {
    setAddress(place.address);
    setLatitude(place.lat);
    setLongitude(place.lng);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!jobName.trim()) {
      toast.error("Job name is required");
      return;
    }
    if (!address.trim()) {
      toast.error("Job address is required");
      return;
    }
    if (!jobType) {
      toast.error("Job type is required");
      return;
    }
    
    if (!orgId) {
      toast.error("Organization not found");
      return;
    }

    setLoading(true);

    try {
      const jobNumber = generateJobNumber();

      const jobData = {
        org_id: orgId,
        job_number: jobNumber,
        owner_name: jobName,
        owner_email: ownerEmail || null,
        owner_phone: ownerPhone || null,
        property_address: address,
        latitude,
        longitude,
        property_type: jobType,
        insurance_company: insuranceCompany || null,
        claim_number: claimNumber || null,
        adjuster_name: adjusterName || null,
        adjuster_phone: adjusterPhone || null,
        adjuster_email: adjusterEmail || null,
        policy_number: policyNumber || null,
        roof_type: roofType || null,
        roof_age: roofAge ? parseInt(roofAge) : null,
        status: 'new' as const,
        created_by: user.id,
      };

      const { data: job, error } = await supabase
        .from('jobs')
        .insert([jobData])
        .select()
        .single();

      if (error) throw error;

      // Create job_trades entries for each selected trade
      const jobTradesData = selectedTrades.map(tradeCode => ({
        job_id: job.id,
        org_id: orgId,
        trade_code: tradeCode,
        status: 'new',
        subtotal: 0,
      }));

      const { error: tradesError } = await supabase
        .from('job_trades')
        .insert(jobTradesData);

      if (tradesError) {
        console.error("Error creating job trades:", tradesError);
        // Don't fail the whole operation, just log it
      }

      // Create timeline event
      await supabase.from('job_timeline').insert({
        job_id: job.id,
        org_id: orgId,
        event_type: 'job_created',
        description: `Job created: ${jobName}`,
        created_by: user.id,
      });

      toast.success("Job created successfully!");
      navigate(`/jobs/${job.id}`);
    } catch (error: any) {
      console.error("Error creating job:", error);
      toast.error(error.message || "Failed to create job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 py-8">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Create New Job
          </h1>
          <p className="text-muted-foreground mt-2">
            Add a new job to your pipeline
          </p>
        </div>

        <Card className="border-border/50 bg-card/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-xl">Job Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Required Fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="jobName">
                    Contact Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="jobName"
                    value={jobName}
                    onChange={(e) => setJobName(e.target.value)}
                    placeholder="John Smith"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">
                    Property Address <span className="text-destructive">*</span>
                  </Label>
                  <AddressAutocomplete
                    value={address}
                    onChange={setAddress}
                    onPlaceSelected={handlePlaceSelected}
                    isLoaded={isLoaded}
                  />
                </div>
              </div>

              {/* Basic Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ownerEmail">Email</Label>
                  <Input
                    id="ownerEmail"
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    placeholder="john@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ownerPhone">Phone</Label>
                  <Input
                    id="ownerPhone"
                    value={ownerPhone}
                    onChange={(e) => setOwnerPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Job Type */}
              <div className="space-y-2">
                <Label htmlFor="jobType">
                  Job Type <span className="text-destructive">*</span>
                </Label>
                <Select value={jobType} onValueChange={setJobType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select job type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Insurance">Insurance</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Trade Selection */}
              <div className="space-y-2">
                <Label>
                  Trade Scopes <span className="text-destructive">*</span>
                </Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select all trades that will be part of this project
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {availableTrades.map((trade) => (
                    <label
                      key={trade.code}
                      className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-accent transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTrades.includes(trade.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTrades([...selectedTrades, trade.code]);
                          } else {
                            // Don't allow unchecking if it's the last trade
                            if (selectedTrades.length > 1) {
                              setSelectedTrades(selectedTrades.filter(t => t !== trade.code));
                            } else {
                              toast.error("At least one trade must be selected");
                            }
                          }
                        }}
                        className="rounded border-border"
                      />
                      <span className="text-lg">{trade.icon}</span>
                      <span className="font-medium">{trade.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Insurance Fields - Collapsible */}
              {jobType === "Insurance" && (
                <Collapsible open={insuranceOpen} onOpenChange={setInsuranceOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between" type="button">
                      <span className="font-medium">Insurance Details (Optional)</span>
                      <ChevronDown className={cn("h-4 w-4 transition-transform", insuranceOpen && "rotate-180")} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="insuranceCompany">Insurance Company</Label>
                        <Input
                          id="insuranceCompany"
                          value={insuranceCompany}
                          onChange={(e) => setInsuranceCompany(e.target.value)}
                          placeholder="State Farm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="claimNumber">Claim Number</Label>
                        <Input
                          id="claimNumber"
                          value={claimNumber}
                          onChange={(e) => setClaimNumber(e.target.value)}
                          placeholder="CLM-123456"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="policyNumber">Policy Number</Label>
                      <Input
                        id="policyNumber"
                        value={policyNumber}
                        onChange={(e) => setPolicyNumber(e.target.value)}
                        placeholder="POL-789012"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="adjusterName">Adjuster Name</Label>
                      <Input
                        id="adjusterName"
                        value={adjusterName}
                        onChange={(e) => setAdjusterName(e.target.value)}
                        placeholder="Jane Doe"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="adjusterPhone">Adjuster Phone</Label>
                        <Input
                          id="adjusterPhone"
                          value={adjusterPhone}
                          onChange={(e) => setAdjusterPhone(e.target.value)}
                          placeholder="(555) 987-6543"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="adjusterEmail">Adjuster Email</Label>
                        <Input
                          id="adjusterEmail"
                          type="email"
                          value={adjusterEmail}
                          onChange={(e) => setAdjusterEmail(e.target.value)}
                          placeholder="adjuster@insurance.com"
                        />
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Property Details - Collapsible */}
              <Collapsible open={propertyOpen} onOpenChange={setPropertyOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" type="button">
                    <span className="font-medium">Property Details (Optional)</span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", propertyOpen && "rotate-180")} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="roofType">Roof Type</Label>
                      <Select value={roofType} onValueChange={setRoofType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Asphalt Shingle">Asphalt Shingle</SelectItem>
                          <SelectItem value="Metal">Metal</SelectItem>
                          <SelectItem value="Tile">Tile</SelectItem>
                          <SelectItem value="Flat">Flat</SelectItem>
                          <SelectItem value="TPO">TPO</SelectItem>
                          <SelectItem value="EPDM">EPDM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="roofAge">Roof Age (years)</Label>
                      <Input
                        id="roofAge"
                        type="number"
                        value={roofAge}
                        onChange={(e) => setRoofAge(e.target.value)}
                        placeholder="10"
                        min="0"
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate("/jobs")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Job"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateJob;